import React, {useState, useEffect, useRef} from 'react';

function AdminAIChat({ defaultModel = 'gpt-5-mini', defaultVerbosity='low', defaultEffort='low' }) {
    // Conversation state: [{role: 'user'|'assistant'|'system'|'tool', content: string}]
    const [messages, setMessages] = useState([
        {
            role: "user",
            content: "Hello! I have some questions about Mayhem Marketplace. Can you help me? Also, this is an example message, so we need to make it long to trigger scrolling.",
        },
        {
            role: "assistant",
            content: "Hi! I'm here to help you with any questions you have about Mayhem Marketplace. Feel free to ask me anything! For example, you can ask about how to list a product, manage your account, or understand our fee structure. Just let me know what you need assistance with.",
        },
        {
            role: "user",
            content: "Great! Can you tell me how to list a new product on Mayhem Marketplace?",
        },
        {
            role: "assistant",
            content: "Sure! To list a new product on Mayhem Marketplace, follow these steps:\n\n1. Log in to your seller account.\n2. Navigate to the 'Products' section in your dashboard.\n3. Click on the 'Add New Product' button.\n4. Fill in the product details, including title, description, price, and images.\n5. Select the appropriate category for your product.\n6. Set your shipping options and return policy.\n7. Review all the information to ensure it's accurate.\n8. Click 'Submit' to list your product.\n\nOnce submitted, your product will be reviewed and should go live within 24-48 hours. If you have any more questions or need further assistance, feel free to ask!",
        }
    ]);
    const [input, setInput] = useState('');
    const [instructions, setInstructions] = useState('You are a helpful assistant that knows everything about Mayhem Marketplace. Prefer concise answers and include relevant links to internal docs when helpful.');
    const [model, setModel] = useState(defaultModel);
    const [verbosity, setVerbosity] = useState(defaultVerbosity);
    const [effort, setEffort] = useState(defaultEffort);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [configOpen, setConfigOpen] = useState(false);
    const inputRef = useRef(null);
    const bottomRef = useRef(null);

    const canSend = input.trim().length > 0 && !isLoading;

    const scrollToBottom = () => {
        try { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); } catch (e) {}
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    const handleSend = async () => {
        if (!canSend) return;
        const userMsg = { role: 'user', content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);
        setError(null);
        try {
            const allInput = [
                ...messages.map(m => ({ role: m.role, content: m.content })),
                userMsg
            ]
            const res = await fetch('/admin/ai/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({
                    input: allInput,
                    instructions,
                    model,
                    verbosity,
                    effort
                })
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                throw new Error(data?.error || `Request failed with status ${res.status}`);
            }
            // Try to extract assistant text from OpenAI-like response
            let assistantText = '';
            const resp = data.response;
            if (resp?.output_text) {
                assistantText = resp.output_text;
            }
            if (!assistantText) {
                // Fallback: show serialized response chunk
                assistantText = 'Received response, but could not parse text. Raw:\n' + JSON.stringify(resp, null, 2);
            }
            setMessages(prev => [...prev, { role: 'assistant', content: assistantText, response: resp }]);
        } catch (e) {
            setError(e.message);
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleKeyDown = (e) => {
        if ((e.key === 'Enter' || e.keyCode === 13) && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClear = () => {
        setMessages([]);
        setError(null);
        setInput('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const styles = {
        container: { display: 'flex', flexDirection: 'column', gap: '12px' },
        config: { display: configOpen ? 'flex' : 'none', gap: '8px', flexWrap: 'wrap', marginBottom: 0, padding: '12px 0', borderBottom: '1px solid #ddd' },
        label: { fontWeight: 600, display: 'block', marginBottom: 4 },
        configToggle: { cursor: 'pointer', background: '#f6f6f6', border: '1px solid #ddd', borderRadius: 6, padding: '6px 12px', fontWeight: 600, marginBottom: 0, display: 'inline-block' },
        configPanel: { marginBottom: 0 },
        option: { minWidth: 120, marginRight: 8, marginBottom: 0 },
        inputWrapper: { display: 'flex', gap: '8px', alignItems: 'center' },
        input: {
            flex: 1, padding: '8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14,
            fontFamily: 'inherit', resize: 'none' },
        nameTag: {
            width:28, height:28, borderRadius:'50%',
            color:'#fff', fontSize:12, flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontWeight:600, textAlign:'center',
            userSelect:'none',
            background:'#6c757d' // default gray, overridden for assistant
        },
        messageContent: {
            whiteSpace: 'pre-wrap',
            fontSize: 13,
        }
    };


    return (
        <div className="ai-chat-container" style={styles.container}>
            <div style={styles.configPanel}>
                <button
                    type="button"
                    style={styles.configToggle}
                    aria-expanded={configOpen}
                    aria-controls="ai-chat-config-panel"
                    onClick={()=>setConfigOpen(o=>!o)}
                >
                    {configOpen ? 'Hide Chat Settings ▲' : 'Show Chat Settings ▼'}
                </button>
                <div
                    id="ai-chat-config-panel"
                    className="ai-chat-config"
                    style={styles.config}
                >
                    <div style={styles.option}>
                        <label style={styles.label}>Model</label>
                        <select value={model} onChange={(e)=>setModel(e.target.value)}>
                            <option value="gpt-5-mini">gpt-5-mini (default)</option>
                            <option value="gpt-5">gpt-5</option>
                            <option value="gpt-5-nano">gpt-5-nano</option>
                        </select>
                    </div>
                    <div style={styles.option}>
                        <label style={styles.label}>Verbosity</label>
                        <select value={verbosity} onChange={(e)=>setVerbosity(e.target.value)}>
                            <option value="low">low</option>
                            <option value="medium">medium</option>
                            <option value="high">high</option>
                        </select>
                    </div>
                    <div style={styles.option}>
                        <label style={styles.label}>Effort</label>
                        <select value={effort} onChange={(e)=>setEffort(e.target.value)}>
                            <option value="low">low</option>
                            <option value="medium">medium</option>
                            <option value="high">high</option>
                        </select>
                    </div>
                    <div style={{...styles.option, flex: 3}}>
                        <label style={styles.label}>Instructions</label>
                        <textarea value={instructions} onChange={(e)=>setInstructions(e.target.value)} placeholder="System instructions" style={{width:'100%'}} />
                    </div>
                    <button type="button" className="btn" onClick={handleClear} disabled={isLoading}>Clear</button>
                </div>
            </div>

            <div className="ai-chat-window" style={{border:'1px solid #ddd', borderRadius:8, padding:12, minHeight:320, maxHeight:480, overflowY:'auto'}}>
                {messages.length === 0 && (
                    <div style={{opacity:0.7}}>Ask a question to get started.</div>
                )}
                {messages.map((m, idx) => (
                    <div key={idx} style={{
                        display:'flex', gap:8, marginBottom:10,
                        alignItems:'flex-start'
                    }}>
                        <div style={{
                            ...styles.nameTag,
                            background: m.role === 'assistant' ? '#0d6efd' : '#6c757d',
                        }}>{m.role === 'assistant' ? 'AI' : 'You'}</div>
                        <div style={styles.messageContent}>{m.content}</div>
                    </div>
                ))}
                {isLoading && (
                    <div style={{display:'flex', gap:8, alignItems:'center', opacity:0.7}}>
                        <div className="spinner" style={{width:16, height:16, border:'2px solid #ccc', borderTopColor:'#333', borderRadius:'50%', animation:'spin 1s linear infinite'}} />
                        <div>Thinking…</div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {error && (
                <div className="alert alert-danger" style={{color:'#842029', background:'#f8d7da', padding:8, borderRadius:6}}>
                    {error}
                </div>
            )}

            <div className="ai-chat-input" style={styles.inputWrapper}>
                <textarea
                    ref={inputRef}
                    rows={3}
                    value={input}
                    onChange={(e)=>setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your question and press Enter (Shift+Enter for newline)…"
                    style={styles.input}
                />
                <button type="button" className="btn btn-primary" onClick={handleSend} disabled={!canSend}>
                    {isLoading ? 'Sending…' : 'Send'}
                </button>
            </div>
            {/* simple spinner keyframes */}
            <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
        </div>
    );
}

export default AdminAIChat;